import {Timezone} from 'common/constants';
import {ProjectFeatureFlag} from 'common/report/constants';
import {TimePeriodSettings} from 'common/report/stores/types';
import {Translatable} from 'common/i18n';
import {
  endOfFiscalYear,
  endOfQuarter,
  endOfWeek,
  getCurrentMomentAsOf,
  ISO_WEEKDAY_FROM_SETTING_WEEKDAY,
  SettingWeekday,
  startOfFiscalYear,
  startOfQuarter,
  startOfWeek,
} from 'common/util/date';

import {DateRangeAttributes, DateRangeType, TimeUnit} from './types';

export enum PresetDateRange {
  Custom = `custom`,
  Today = `today`,
  Yesterday = `yesterday`,
  Last1Day = `last_1_day`,
  Last96Hours = `last_96_hours`,
  Last7Days = `last_7_days`,
  Last30Days = `last_30_days`,
  Last3Months = `last_3_months`,
  Last24Weeks = `last_24_weeks`,
  Last6Months = `last_6_months`,
  Last12Months = `last_12_months`,
  WeekToDate = `week_to_date`,
  MonthToDate = `month_to_date`,
  QuarterToDate = `quarter_to_date`,
  YearToDate = `year_to_date`,
  PreviousWeek = `previous_week`,
  PreviousMonth = `previous_month`,
  PreviousQuarter = `previous_quarter`,
  PreviousYear = `previous_year`,
}

export const SHOW_LAST_X_DAYS_PRESET_DATE_RANGE = {
  [ProjectFeatureFlag.ShowLast1Day]: PresetDateRange.Last1Day,
  [ProjectFeatureFlag.ShowLast7Days]: PresetDateRange.Last7Days,
};

export const TIME_UNIT_DISPLAY_NAMES: {[key in TimeUnit]: string} = {
  [TimeUnit.Second]: `seconds`,
  [TimeUnit.Minute]: `minutes`,
  [TimeUnit.Hour]: `hours`,
  [TimeUnit.Day]: `days`,
  [TimeUnit.Week]: `weeks`,
  [TimeUnit.Month]: `months`,
  [TimeUnit.Quarter]: `quarters`,
  [TimeUnit.Year]: `years`,
  [TimeUnit.Session]: `sessions`,
  // Not sure about those two. added special time unit into TimeUnit because it's actually being used
  [TimeUnit.DayOfWeek]: `days`,
  [TimeUnit.HourOfDay]: `hours`,
};

export const DATE_RANGE_TYPE_DISPLAY_NAMES: {[key in DateRangeType]: Translatable} = {
  [DateRangeType.Between]: ({t}) => t(`reports.date-range.type.between`),
  [DateRangeType.NotBetween]: ({t}) => t(`reports.date-range.type.not-between`),
  [DateRangeType.On]: ({t}) => t(`reports.date-range.type.on`),
  [DateRangeType.NotOn]: ({t}) => t(`reports.date-range.type.not-on`),
  [DateRangeType.RelativeAfter]: ({t}) => t(`reports.date-range.type.relative-after`),
  [DateRangeType.RelativeBefore]: ({t}) => t(`reports.date-range.type.relative-before`),
  [DateRangeType.Since]: ({t}) => t(`reports.date-range.type.since`),
  [DateRangeType.StartsOn]: ({t}) => t(`reports.date-range.type.starts-on`),
  [DateRangeType.EndsOn]: ({t}) => t(`reports.date-range.type.ends-on`),
  [DateRangeType.RelativeFuture]: ({t}) => t(`reports.date-range.type.relative-future`),
  [DateRangeType.NotRelativeAfter]: ({t}) => t(`reports.date-range.type.not-relative-after`),
  [DateRangeType.Before]: ({t}) => t(`reports.date-range.type.before`),
};

export const NOW = `$now`;
export const TODAY = `$today`;

export const END_OF_TODAY = `$end_of_current_day`;
export const END_OF_YESTERDAY = `$end_of_previous_day`;
export const END_OF_PREVIOUS_WEEK = `$end_of_previous_week`;
export const END_OF_PREVIOUS_MONTH = `$end_of_previous_month`;
export const END_OF_PREVIOUS_QUARTER = `$end_of_previous_quarter`;
export const END_OF_PREVIOUS_YEAR = `$end_of_previous_year`;

export const START_OF_WEEK = `$start_of_week`;
export const START_OF_MONTH = `$start_of_month`;
export const START_OF_TODAY = `$start_of_current_day`;
export const START_OF_QUARTER = `$start_of_quarter`;
export const START_OF_YEAR = `$start_of_year`;
export const START_OF_YESTERDAY = `$start_of_previous_day`;
export const START_OF_PREVIOUS_WEEK = `$start_of_previous_week`;
export const START_OF_PREVIOUS_MONTH = `$start_of_previous_month`;
export const START_OF_PREVIOUS_QUARTER = `$start_of_previous_quarter`;
export const START_OF_PREVIOUS_YEAR = `$start_of_previous_year`;

export const PERIOD_TO_DATE = [START_OF_WEEK, START_OF_MONTH, START_OF_QUARTER, START_OF_YEAR];
export const PREVIOUS_PERIOD_PRESETS = [
  START_OF_PREVIOUS_WEEK,
  START_OF_PREVIOUS_MONTH,
  START_OF_PREVIOUS_QUARTER,
  START_OF_PREVIOUS_YEAR,
];
export const PERIOD_PRESET_UNITS = [TimeUnit.Week, TimeUnit.Month, TimeUnit.Quarter, TimeUnit.Year];
export type PeriodStartDateString =
  | typeof START_OF_TODAY
  | typeof START_OF_YESTERDAY
  | typeof START_OF_WEEK
  | typeof START_OF_MONTH
  | typeof START_OF_QUARTER
  | typeof START_OF_YEAR
  | typeof START_OF_PREVIOUS_WEEK
  | typeof START_OF_PREVIOUS_MONTH
  | typeof START_OF_PREVIOUS_QUARTER
  | typeof START_OF_PREVIOUS_YEAR;
export type SpecialDateString =
  | typeof START_OF_YESTERDAY
  | typeof END_OF_YESTERDAY
  | typeof START_OF_TODAY
  | typeof END_OF_TODAY
  | typeof TODAY
  | typeof NOW
  | typeof START_OF_WEEK
  | typeof START_OF_MONTH
  | typeof START_OF_QUARTER
  | typeof START_OF_YEAR
  | typeof START_OF_PREVIOUS_WEEK
  | typeof START_OF_PREVIOUS_MONTH
  | typeof START_OF_PREVIOUS_QUARTER
  | typeof START_OF_PREVIOUS_YEAR
  | typeof END_OF_PREVIOUS_WEEK
  | typeof END_OF_PREVIOUS_MONTH
  | typeof END_OF_PREVIOUS_QUARTER
  | typeof END_OF_PREVIOUS_YEAR;

export const SPECIAL_DATE_RESOLVERS: {
  [Key in SpecialDateString]: (
    asOfDate: Date | null,
    projectTz: Timezone,
    timePeriodSettings: TimePeriodSettings,
  ) => Date;
} = {
  [START_OF_YESTERDAY]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).subtract(1, `days`).startOf(`day`).toDate(),
  [END_OF_YESTERDAY]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).subtract(1, `days`).endOf(`day`).toDate(),
  [START_OF_TODAY]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).startOf(`day`).toDate(),
  [END_OF_TODAY]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).endOf(`day`).toDate(),
  [TODAY]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).startOf(`day`).toDate(),
  [NOW]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).toDate(),
  [START_OF_WEEK]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return startOfWeek(
      curMoment,
      projectTz,
      ISO_WEEKDAY_FROM_SETTING_WEEKDAY[timePeriodSettings.weekStartDay as SettingWeekday],
    );
  },
  [START_OF_MONTH]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).startOf(`month`).toDate(),
  [START_OF_QUARTER]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return startOfQuarter(curMoment, projectTz, timePeriodSettings.quarterStartMonth);
  },
  [START_OF_YEAR]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return startOfFiscalYear(curMoment, projectTz, timePeriodSettings.quarterStartMonth);
  },
  [START_OF_PREVIOUS_WEEK]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return startOfWeek(
      curMoment.subtract(7, `days`),
      projectTz,
      ISO_WEEKDAY_FROM_SETTING_WEEKDAY[timePeriodSettings.weekStartDay as SettingWeekday],
    );
  },
  [START_OF_PREVIOUS_MONTH]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).subtract(1, `month`).startOf(`month`).toDate(),
  [START_OF_PREVIOUS_QUARTER]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return startOfQuarter(curMoment.subtract(3, `month`), projectTz, timePeriodSettings.quarterStartMonth);
  },
  [START_OF_PREVIOUS_YEAR]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return startOfFiscalYear(curMoment.subtract(1, `year`), projectTz, timePeriodSettings.quarterStartMonth);
  },
  [END_OF_PREVIOUS_WEEK]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return endOfWeek(
      curMoment.subtract(7, `days`),
      projectTz,
      ISO_WEEKDAY_FROM_SETTING_WEEKDAY[timePeriodSettings.weekStartDay as SettingWeekday],
    );
  },
  [END_OF_PREVIOUS_MONTH]: (asOfDate: Date | null, projectTz: Timezone, _: TimePeriodSettings) =>
    getCurrentMomentAsOf(asOfDate, projectTz).subtract(1, `month`).endOf(`month`).toDate(),
  [END_OF_PREVIOUS_QUARTER]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return endOfQuarter(curMoment.subtract(3, `month`), projectTz, timePeriodSettings.quarterStartMonth);
  },
  [END_OF_PREVIOUS_YEAR]: (asOfDate: Date | null, projectTz: Timezone, timePeriodSettings: TimePeriodSettings) => {
    const curMoment = getCurrentMomentAsOf(asOfDate, projectTz);
    return endOfFiscalYear(curMoment.subtract(1, `year`), projectTz, timePeriodSettings.quarterStartMonth);
  },
};

export const PRESET_DATE_RANGE_DISPLAY_NAMES: {[key in PresetDateRange]: Translatable} = {
  [PresetDateRange.Today]: ({t}) => t(`reports.date-range.presets.description.today`),
  [PresetDateRange.Yesterday]: ({t}) => t(`reports.date-range.presets.description.yesterday`),
  [PresetDateRange.Last1Day]: ({t}) => t(`reports.date-range.presets.description.1-day`),
  [PresetDateRange.Last96Hours]: ({t}) => t(`reports.date-range.presets.description.96-hours`),
  [PresetDateRange.Last7Days]: ({t}) => t(`reports.date-range.presets.description.7-days`),
  [PresetDateRange.Last30Days]: ({t}) => t(`reports.date-range.presets.description.30-days`),
  [PresetDateRange.Last3Months]: ({t}) => t(`reports.date-range.presets.description.3-months`),
  [PresetDateRange.Last24Weeks]: ({t}) => t(`reports.date-range.presets.description.24-weeks`),
  [PresetDateRange.Last6Months]: ({t}) => t(`reports.date-range.presets.description.6-months`),
  [PresetDateRange.Last12Months]: ({t}) => t(`reports.date-range.presets.description.12-months`),
  [PresetDateRange.Custom]: ({t}) => t(`reports.date-range.presets.description.custom`),
  [PresetDateRange.WeekToDate]: ({t}) => t(`reports.date-range.presets.description.week-to-date`),
  [PresetDateRange.MonthToDate]: ({t}) => t(`reports.date-range.presets.description.month-to-date`),
  [PresetDateRange.QuarterToDate]: ({t}) => t(`reports.date-range.presets.description.quarter-to-date`),
  [PresetDateRange.YearToDate]: ({t}) => t(`reports.date-range.presets.description.year-to-date`),
  [PresetDateRange.PreviousWeek]: ({t}) => t(`reports.date-range.presets.previous-week`),
  [PresetDateRange.PreviousMonth]: ({t}) => t(`reports.date-range.presets.previous-month`),
  [PresetDateRange.PreviousQuarter]: ({t}) => t(`reports.date-range.presets.previous-quarter`),
  [PresetDateRange.PreviousYear]: ({t}) => t(`reports.date-range.presets.previous-year`),
};

export const PRESET_DATE_RANGE_SHORT_DISPLAY_NAMES: {[key in PresetDateRange]: Translatable} = {
  [PresetDateRange.Today]: ({t}) => t(`reports.date-range.presets.today`),
  [PresetDateRange.Yesterday]: ({t}) => t(`reports.date-range.presets.yesterday`),
  [PresetDateRange.Last1Day]: ({t}) => t(`reports.date-range.presets.1-day`),
  [PresetDateRange.Last96Hours]: ({t}) => t(`reports.date-range.presets.96-hours`),
  [PresetDateRange.Last7Days]: ({t}) => t(`reports.date-range.presets.7-days`),
  [PresetDateRange.Last30Days]: ({t}) => t(`reports.date-range.presets.30-days`),
  [PresetDateRange.Last3Months]: ({t}) => t(`reports.date-range.presets.3-months`),
  [PresetDateRange.Last24Weeks]: ({t}) => t(`reports.date-range.presets.24-weeks`),
  [PresetDateRange.Last6Months]: ({t}) => t(`reports.date-range.presets.6-months`),
  [PresetDateRange.Last12Months]: ({t}) => t(`reports.date-range.presets.12-months`),
  [PresetDateRange.Custom]: ({t}) => t(`reports.date-range.presets.custom`),
  [PresetDateRange.WeekToDate]: ({t}) => t(`reports.date-range.presets.week-to-date`),
  [PresetDateRange.MonthToDate]: ({t}) => t(`reports.date-range.presets.month-to-date`),
  [PresetDateRange.QuarterToDate]: ({t}) => t(`reports.date-range.presets.quarter-to-date`),
  [PresetDateRange.YearToDate]: ({t}) => t(`reports.date-range.presets.year-to-date`),
  [PresetDateRange.PreviousWeek]: ({t}) => t(`reports.date-range.presets.previous-week`),
  [PresetDateRange.PreviousMonth]: ({t}) => t(`reports.date-range.presets.previous-month`),
  [PresetDateRange.PreviousQuarter]: ({t}) => t(`reports.date-range.presets.previous-quarter`),
  [PresetDateRange.PreviousYear]: ({t}) => t(`reports.date-range.presets.previous-year`),
};

export const PRESET_DATE_RANGE_OPTIONS: PresetDateRange[] = [
  PresetDateRange.Today,
  PresetDateRange.Yesterday,
  PresetDateRange.Last1Day,
  PresetDateRange.Last96Hours,
  PresetDateRange.Last7Days,
  PresetDateRange.Last30Days,
  PresetDateRange.Last3Months,
  PresetDateRange.Last24Weeks,
  PresetDateRange.Last6Months,
  PresetDateRange.Last12Months,
  PresetDateRange.Custom,
  PresetDateRange.WeekToDate,
  PresetDateRange.MonthToDate,
  PresetDateRange.QuarterToDate,
  PresetDateRange.YearToDate,
];

export const PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS = {
  [PresetDateRange.Today]: {
    from: START_OF_TODAY,
    to: END_OF_TODAY,
    type: DateRangeType.Between,
  },
  [PresetDateRange.Yesterday]: {
    from: START_OF_YESTERDAY,
    to: END_OF_YESTERDAY,
    type: DateRangeType.Between,
  },
  [PresetDateRange.Last1Day]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Day,
      value: 1,
    },
  },
  [PresetDateRange.Last96Hours]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Hour,
      value: 96,
    },
  },
  [PresetDateRange.Last7Days]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Day,
      value: 7,
    },
  },
  [PresetDateRange.Last30Days]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Day,
      value: 30,
    },
  },
  [PresetDateRange.Last3Months]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Month,
      value: 3,
    },
  },
  [PresetDateRange.Last24Weeks]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Week,
      value: 24,
    },
  },
  [PresetDateRange.Last6Months]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Month,
      value: 6,
    },
  },
  [PresetDateRange.Last12Months]: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Month,
      value: 12,
    },
  },
  [PresetDateRange.WeekToDate]: {
    from: START_OF_WEEK,
    type: DateRangeType.Since,
  },
  [PresetDateRange.MonthToDate]: {
    from: START_OF_MONTH,
    type: DateRangeType.Since,
  },
  [PresetDateRange.QuarterToDate]: {
    from: START_OF_QUARTER,
    type: DateRangeType.Since,
  },
  [PresetDateRange.YearToDate]: {
    from: START_OF_YEAR,
    type: DateRangeType.Since,
  },
  [PresetDateRange.PreviousWeek]: {
    from: START_OF_PREVIOUS_WEEK,
    to: END_OF_PREVIOUS_WEEK,
    type: DateRangeType.Between,
  },
  [PresetDateRange.PreviousMonth]: {
    from: START_OF_PREVIOUS_MONTH,
    to: END_OF_PREVIOUS_MONTH,
    type: DateRangeType.Between,
  },
  [PresetDateRange.PreviousQuarter]: {
    from: START_OF_PREVIOUS_QUARTER,
    to: END_OF_PREVIOUS_QUARTER,
    type: DateRangeType.Between,
  },
  [PresetDateRange.PreviousYear]: {
    from: START_OF_PREVIOUS_YEAR,
    to: END_OF_PREVIOUS_YEAR,
    type: DateRangeType.Between,
  },
};

export const PERIOD_START_TO_PRESET_DATE_RANGE: {[key in PeriodStartDateString]: PresetDateRange} = {
  [START_OF_TODAY]: PresetDateRange.Today,
  [START_OF_YESTERDAY]: PresetDateRange.Yesterday,
  [START_OF_WEEK]: PresetDateRange.WeekToDate,
  [START_OF_MONTH]: PresetDateRange.MonthToDate,
  [START_OF_QUARTER]: PresetDateRange.QuarterToDate,
  [START_OF_YEAR]: PresetDateRange.YearToDate,
  [START_OF_PREVIOUS_WEEK]: PresetDateRange.PreviousWeek,
  [START_OF_PREVIOUS_MONTH]: PresetDateRange.PreviousMonth,
  [START_OF_PREVIOUS_QUARTER]: PresetDateRange.PreviousQuarter,
  [START_OF_PREVIOUS_YEAR]: PresetDateRange.PreviousYear,
};

export const TIME_COMPARISON_OPTIONS = {
  PREVIOUS_DAY: `previous_day`,
  PREVIOUS_WEEK: `previous_week`,
  PREVIOUS_MONTH: `previous_month`,
  PREVIOUS_QUARTER: `previous_quarter`,
  PREVIOUS_YEAR: `previous_year`,
  CUSTOM: `custom`,
};

type ComparisonTimeUnit =
  | typeof TimeUnit.Day
  | typeof TimeUnit.Week
  | typeof TimeUnit.Month
  | typeof TimeUnit.Quarter
  | typeof TimeUnit.Year;
export const TIME_COMPARISON_OPTION_LABELS: {[key in ComparisonTimeUnit]: Translatable} = {
  [TimeUnit.Day]: ({t}) => t(`reports.lift-comparison.previous-period.day`),
  [TimeUnit.Week]: ({t}) => t(`reports.lift-comparison.previous-period.week`),
  [TimeUnit.Month]: ({t}) => t(`reports.lift-comparison.previous-period.month`),
  [TimeUnit.Quarter]: ({t}) => t(`reports.lift-comparison.previous-period.quarter`),
  [TimeUnit.Year]: ({t}) => t(`reports.lift-comparison.previous-period.year`),
};

export const TO_DATE_OPTIONS = [
  PresetDateRange.WeekToDate,
  PresetDateRange.MonthToDate,
  PresetDateRange.QuarterToDate,
  PresetDateRange.YearToDate,
];

export function isTodayPreset(dateRange: DateRangeAttributes | undefined): boolean {
  if (!dateRange || !dateRange.from || !dateRange.to) {
    return false;
  }

  const todayPresetParams = PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.Today];
  return (
    dateRange.from === todayPresetParams.from &&
    dateRange.to === todayPresetParams.to &&
    dateRange.type === todayPresetParams.type
  );
}

export function isYesterdayPreset(dateRange: DateRangeAttributes | undefined): boolean {
  if (!dateRange || !dateRange.from || !dateRange.to) {
    return false;
  }

  const yesterdayPresetParams = PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.Yesterday];
  return (
    dateRange.from === yesterdayPresetParams.from &&
    dateRange.to === yesterdayPresetParams.to &&
    dateRange.type === yesterdayPresetParams.type
  );
}

export function isPreviousPeriodPreset(dateRange: DateRangeAttributes | undefined): boolean {
  if (dateRange?.from && (dateRange.from as string)) {
    return PREVIOUS_PERIOD_PRESETS.includes(dateRange?.from as string);
  }
  return false;
}

export function isPeriodToDate(dateRange: DateRangeAttributes | undefined): boolean {
  if (dateRange?.from && (dateRange.from as string)) {
    return PERIOD_TO_DATE.includes(dateRange?.from as string);
  }
  return false;
}

export function getPresetDateRangeFromAttributes(
  dateRange: DateRangeAttributes | undefined,
): PresetDateRange | undefined {
  if (!dateRange) {
    return undefined;
  }

  if (dateRange?.from as PeriodStartDateString) {
    return PERIOD_START_TO_PRESET_DATE_RANGE[dateRange.from as PeriodStartDateString];
  }
  if (dateRange.window?.value === 7 && dateRange.window?.unit === TimeUnit.Day) {
    return PresetDateRange.Last7Days;
  }
  if (dateRange.window?.value === 30 && dateRange.window?.unit === TimeUnit.Day) {
    return PresetDateRange.Last30Days;
  }
  if (dateRange.window?.value === 3 && dateRange.window?.unit === TimeUnit.Month) {
    return PresetDateRange.Last3Months;
  }
  if (dateRange.window?.value === 6 && dateRange.window?.unit === TimeUnit.Month) {
    return PresetDateRange.Last6Months;
  }
  if (dateRange.window?.value === 12 && dateRange.window?.unit === TimeUnit.Month) {
    return PresetDateRange.Last12Months;
  }

  return undefined;
}

export function unitToPeriodToDateAttributes(unit: TimeUnit): DateRangeAttributes {
  switch (unit) {
    case TimeUnit.Week:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.WeekToDate];
    case TimeUnit.Month:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.MonthToDate];
    case TimeUnit.Quarter:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.QuarterToDate];
    case TimeUnit.Year:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.YearToDate];
    default:
      throw new Error(`unsupported time unit option for period to date`);
  }
}

export function unitToPreviousPeriodAttributes(unit: TimeUnit): DateRangeAttributes {
  switch (unit) {
    case TimeUnit.Week:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.PreviousWeek];
    case TimeUnit.Month:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.PreviousMonth];
    case TimeUnit.Quarter:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.PreviousQuarter];
    case TimeUnit.Year:
      return PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[PresetDateRange.PreviousYear];
    default:
      throw new Error(`unsupported time unit option for period to date`);
  }
}
